import { Truck } from 'lucide-react'
import { effektiverStatus, ReceiptOverview } from './ReceiptOverview'
import { ReceiptOrderSelect } from './ReceiptOrderSelect'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useReceipts, useCreateReceipt, useResolveArtikel, useReceiptOrders, downloadPdf } from '@/api/warehouse'
import {
  parsePaste, applyMapping, looksLikeHeader, validateMapping, isPositiveInteger,
  MAPPING_STORAGE_KEY, type PasteGrid, type ColumnMapping,
} from '@/lib/we-paste'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { useAuth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

function localDateStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Task 10 (WE-V2, Spec §4): eine Formular-Zeile der Positions-Tabelle. `status` spiegelt
// die Auto-Aufloesung via useResolveArtikel; `confirmed` ist das explizite OK des Nutzers
// ("ok ok ok" aus der Spec) — erst danach zaehlt die Zeile fuer den Anlage-Button.
interface PositionRow {
  localId: number
  artikel: string
  totalQty: number
  cartonsDelivered: number
  // Eigenes Datum dieser Position (Migration 0094), '' = leer -> Kopf-Datum gilt.
  firstInDate: string
  status: 'pending' | 'ok' | 'error'
  label: string | null
  confirmed: boolean
  lagereinheit?: 'meter' | 'stueck'
  breiteM?: number | null
  rollenlaenge?: number
  rollen?: number
  aufgeloesterArtikel?: string
  aufgeloesterKopf?: number
  importMenge?: number
  importOffen?: boolean
}

function loadStoredMapping(): ColumnMapping {
  try {
    const raw = localStorage.getItem(MAPPING_STORAGE_KEY)
    if (raw) return JSON.parse(raw) as ColumnMapping
  } catch { /* localStorage nicht verfuegbar oder kaputt — Default greift */ }
  return { artikel: 0, menge: 1, kartons: 2 }
}

function colLabel(i: number): string {
  return `Spalte ${String.fromCharCode(65 + i)}`
}

// M3-Fix (Codex Gate B): einheitliche Fehlermeldung für ungültige Menge/Kartons.
const QTY_INVALID_MESSAGE = 'Menge/Kartons müssen ganze Zahlen ≥ 1 sein'

function netzMenge(row: PositionRow): number { return (row.rollenlaenge ?? 0) * (row.rollen ?? 0) * row.cartonsDelivered }
function importRollen(menge: number | undefined, kartons: number, laenge: number): number | null {
  if (!menge || !isPositiveInteger(kartons) || !laenge) return null
  const rollen = menge / kartons / laenge
  return isPositiveInteger(rollen) ? rollen : null
}
function rowQtyValid(row: PositionRow): boolean {
  if (row.lagereinheit === 'meter') return !!row.breiteM && [1, 2, 5, 10, 30].includes(row.rollenlaenge ?? 0) && isPositiveInteger(row.rollen ?? 0) && isPositiveInteger(row.cartonsDelivered) && (row.rollenlaenge !== 30 || row.rollen === 1) && netzMenge(row) <= 2147483647
  return isPositiveInteger(row.totalQty) && isPositiveInteger(row.cartonsDelivered)
}

// Die tatsaechlichen Datenzeilen einer eingefuegten Excel-Tabelle — Kopfzeile
// (looksLikeHeader) wird uebersprungen, sonst landet "Artikel/Menge/Kartons" als
// kaputte erste Position in der Tabelle.
function pasteDataRows(grid: PasteGrid): string[][] {
  if (grid.rows.length > 0 && looksLikeHeader(grid.rows[0]!)) return grid.rows.slice(1)
  return grid.rows
}

export default function ReceiptsPage() {
  const { warehouse } = useAuth()
  const receipts = useReceipts()
  const orders = useReceiptOrders(warehouse.lead)
  const create = useCreateReceipt()
  const resolve = useResolveArtikel()

  const [headerForm, setHeaderForm] = useState({ orderNo: '', firstInDate: localDateStr() })
  const [ordersOpen, setOrdersOpen] = useState(false)
  const [rows, setRows] = useState<PositionRow[]>([])
  const nextLocalId = useRef(1)

  const [pasteText, setPasteText] = useState('')
  const [pasteGrid, setPasteGrid] = useState<PasteGrid | null>(null)
  const [mapping, setMapping] = useState<ColumnMapping>(loadStoredMapping)

  // Race-Guard (Review Task 10): pro Zeile eine laufende Anfrage-Sequenz. Bei
  // schnellem Nachbessern einer Sternnummer kann eine aeltere resolveRow-Antwort
  // NACH einer neueren zurueckkommen (Out-of-Order) und wuerde sonst eine veraltete
  // Aufloesung ueber den bereits geaenderten Wert schreiben.
  const resolveSeqRef = useRef(new Map<number, number>())

  // RA-BI-Muster (siehe alte ReceiptsPage): ein stabiler Key pro Formular-Eingabe,
  // bleibt bei Fehlschlag/Retry unveraendert, wird erst nach echtem Erfolg neu gezogen.
  const createKeyRef = useRef(newIdempotencyKey())

  // M3-Fix (Codex Gate B): zusaetzlich zu confirmed+status muessen Menge/Kartons
  // gueltige positive Ganzzahlen sein — defense-in-depth, falls eine Zeile nach
  // dem OK-Klick ueber einen anderen Pfad nochmal veraendert wurde.
  const allConfirmedOk = rows.length > 0 && rows.every((r) => r.confirmed && r.status === 'ok' && rowQtyValid(r))

  async function resolveRow(localId: number, artikelValue: string) {
    const q = artikelValue.trim()
    // Neue Sequenznummer fuer diese Zeile ziehen — jede spaeter ankommende, aeltere
    // Antwort erkennt sich damit selbst als veraltet und darf nichts mehr schreiben.
    const seq = (resolveSeqRef.current.get(localId) ?? 0) + 1
    resolveSeqRef.current.set(localId, seq)
    if (!q) {
      setRows((rs) => rs.map((r) => (r.localId === localId ? { ...r, status: 'pending', confirmed: false, label: null } : r)))
      return
    }
    setRows((rs) => rs.map((r) => (r.localId === localId ? { ...r, status: 'pending', confirmed: false, label: null } : r)))
    try {
      const res = await resolve.mutateAsync(q)
      const sku = res.artikel.merchantSku ?? res.artikel.sku
      const label = `${sku} — ${res.artikel.name} (DR: ${res.artikel.drQuantity ?? '—'})`
      if (resolveSeqRef.current.get(localId) !== seq) return // ueberholt durch neuere Anfrage
      setRows((rs) => rs.map((r) => {
        if (r.localId !== localId || r.artikel.trim() !== q) return r
        const gleich = r.aufgeloesterArtikel === q && r.aufgeloesterKopf === res.artikel.stockItemId
        const rollenlaenge = gleich ? r.rollenlaenge : res.artikel.rollenVorschlag ?? 0
        const vorschlag = !gleich && r.importOffen ? importRollen(r.importMenge, r.cartonsDelivered, rollenlaenge ?? 0) : null
        return { ...r, status: 'ok', label, lagereinheit: res.artikel.lagereinheit, breiteM: res.artikel.breiteM,
          aufgeloesterArtikel: q, aufgeloesterKopf: res.artikel.stockItemId, rollenlaenge,
          rollen: gleich ? r.rollen : vorschlag ?? 0 }
      }))
    } catch {
      if (resolveSeqRef.current.get(localId) !== seq) return // ueberholt durch neuere Anfrage
      setRows((rs) => rs.map((r) => (r.localId === localId && r.artikel.trim() === q ? { ...r, status: 'error', label: 'nicht gefunden' } : r)))
    }
  }

  function addManualRow() {
    const localId = nextLocalId.current++
    setRows((rs) => [...rs, { localId, artikel: '', totalQty: 0, cartonsDelivered: 0, firstInDate: '', status: 'pending', label: null, confirmed: false }])
  }

  function updateRow(localId: number, patch: Partial<PositionRow>) {
    setRows((rs) => rs.map((r) => (r.localId === localId ? { ...r, ...patch } : r)))
  }

  function removeRow(localId: number) {
    setRows((rs) => rs.filter((r) => r.localId !== localId))
  }

  // M3-Fix (Codex Gate B): OK darf eine Zeile mit ungueltiger Menge/Kartons-Zahl
  // NICHT bestaetigen — stattdessen kippt die Zeile auf 'error' mit Meldung, damit
  // der Anlage-Button (allConfirmedOk) blockiert bleibt.
  function confirmRow(localId: number) {
    setRows((rs) => rs.map((r) => {
      if (r.localId !== localId) return r
      if (!rowQtyValid(r)) return { ...r, status: 'error', confirmed: false, label: QTY_INVALID_MESSAGE }
      return { ...r, confirmed: true }
    }))
  }

  function onPasteTextarea(raw: string) {
    if (!raw.trim()) return
    const grid = parsePaste(raw)
    if (grid.rows.length === 0) return
    setPasteGrid(grid)
    // L4-Fix (Codex Gate B): ein aus localStorage geladenes Mapping kann Indizes
    // enthalten, die zu DIESEM Paste (anderer colCount) nicht mehr passen — vor dem
    // Anzeigen der Zuordnungs-Auswahl gegen den tatsaechlichen colCount validieren.
    setMapping((m) => validateMapping(m, grid.colCount))
  }

  function applyPasteMapping() {
    if (!pasteGrid) return
    try {
      localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(mapping))
    } catch {
      // L4-Fix (Codex Gate B): localStorage kann fehlschlagen (privater Modus,
      // Quota voll, deaktiviert) — das darf den Import selbst nicht abbrechen,
      // nur das Mapping wird dann beim naechsten Mal nicht vorausgefuellt.
    }
    const parsed = applyMapping({ rows: pasteDataRows(pasteGrid), colCount: pasteGrid.colCount }, mapping)
    const newRows: PositionRow[] = parsed.map((p) => {
      const invalid = p.artikel === '' || p.totalQty === null || p.cartons === null
      return {
        localId: nextLocalId.current++,
        artikel: p.artikel,
        totalQty: p.totalQty ?? 0,
        importMenge: p.totalQty ?? undefined,
        importOffen: p.totalQty !== null,
        cartonsDelivered: p.cartons ?? 0,
        firstInDate: '', // eingefuegte Zeilen erben das Kopf-Datum; pro Zeile nachtragbar
        status: invalid ? 'error' : 'pending',
        label: invalid ? 'Artikel/Menge/Kartons nicht lesbar' : null,
        confirmed: false,
      }
    })
    setRows((rs) => [...rs, ...newRows])
    for (const r of newRows) {
      if (r.status === 'pending') void resolveRow(r.localId, r.artikel)
    }
    setPasteGrid(null)
    setPasteText('')
  }

  function cancelPasteMapping() {
    setPasteGrid(null)
    setPasteText('')
  }

  async function onCreate() {
    if (!allConfirmedOk) return
    try {
      const body = {
        // Leeres Positions-Datum bewusst NICHT mitsenden — dann gilt das Kopf-Datum
        // (Backend: item.first_in_date ?? receipt.first_in_date, Migration 0094).
        items: rows.map((r) => ({
          artikel: r.artikel, total_qty: r.lagereinheit === 'meter' ? netzMenge(r) : r.totalQty, cartons_delivered: r.cartonsDelivered,
          ...(r.lagereinheit === 'meter' ? { abmessung: `${r.breiteM!.toFixed(1).replace('.', ',')} m × ${r.rollenlaenge} m` } : {}),
          ...(r.firstInDate ? { first_in_date: r.firstInDate } : {}),
        })),
        idempotency_key: createKeyRef.current,
        ...(headerForm.orderNo ? { order_no: headerForm.orderNo } : {}),
        ...(headerForm.firstInDate ? { first_in_date: headerForm.firstInDate } : {}),
      }
      const r = await create.mutateAsync(body)
      toast.success(`Auftrag ${r.receipt_id} angelegt — ${r.items.length} Position(en)`)
      createKeyRef.current = newIdempotencyKey() // naechster Auftrag braucht einen neuen Key
      setRows([])
      setHeaderForm({ orderNo: '', firstInDate: localDateStr() })
      try {
        await downloadPdf(`/warehouse/receipt/${r.receipt_id}/we-front-pdf/`, `we-front-auftrag-${r.receipt_id}.pdf`)
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'PDF-Download fehlgeschlagen — Etiketten stehen im Auftrag bereit')
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen')
    }
  }

  const receiptRows = receipts.data ?? []
  const selectedOrder = orders.data?.find((o) => o.orderNumber === headerForm.orderNo.trim())
  const openCount = receiptRows.filter((r) => effektiverStatus(r) === 'offen').length
  const partialCount = receiptRows.filter((r) => effektiverStatus(r) === 'teilweise').length
  const lateCount = receiptRows.filter(
    (r) =>
      effektiverStatus(r) !== 'abgeschlossen' &&
      effektiverStatus(r) !== 'vollstaendig' &&
      r.escalation.level === 'eskaliert',
  ).length

  return (
    <div className="p-4 space-y-4 max-w-7xl mx-auto">
      <div className="flex justify-between gap-3 flex-wrap items-center">
        <h1 className="text-xl font-bold">Wareneingang</h1>
        <div className="flex gap-2 text-xs">
          <span className="rounded bg-primary-soft text-primary px-2 py-1">● {openCount} offen</span>
          <span className="rounded bg-amber-100 text-amber-800 px-2 py-1">● {partialCount} teilweise</span>
          {lateCount > 0 && (
            <span className="rounded bg-red-100 text-red-800 px-2 py-1">▲ {lateCount} überfällig</span>
          )}
        </div>
      </div>

      {warehouse.lead && (
        <div className="rounded-xl border border-line bg-panel p-4 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-semibold">Neuer WE-Auftrag</span>
          </div>

          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm">
              WE-Datum
              <Input
                type="date"
                value={headerForm.firstInDate}
                onChange={(e) => setHeaderForm({ ...headerForm, firstInDate: e.target.value })}
              />
            </label>
            <ReceiptOrderSelect
              value={headerForm.orderNo}
              orders={orders.data ?? []}
              open={ordersOpen}
              onOpenChange={setOrdersOpen}
              onChange={(orderNo) => setHeaderForm((current) => ({ ...current, orderNo }))}
            />
            {selectedOrder ? (
              <div className="text-sm">
                <div className="flex items-center gap-1">
                  <Truck size={16} className="text-muted" />
                  <strong>{selectedOrder.lieferant ?? 'Ohne Lieferant'}</strong>{' '}
                  <span>
                    Bestellung {selectedOrder.orderNumber} · {selectedOrder.positionen} {selectedOrder.positionen === 1 ? 'Position' : 'Positionen' }
                  </span>{' '}
                  <Link className="text-primary underline" to={`/einkauf/${selectedOrder.id}`}>
                    Bestellung öffnen
                  </Link>
                </div>
                {selectedOrder.container
                  .filter((c) => ['zugestellt', 'unterwegs'].includes(c.status) && !c.warehouseReceiptId)
                  .map((c) => (
                    <div className="text-muted" key={c.id}>
                      {c.label ?? c.containerNo ?? 'Container'} ist im Einkauf angelegt – WE besser dort über
                      „Zugestellt“ anlegen
                    </div>
                  ))}
              </div>
            ) : (
              !ordersOpen && headerForm.orderNo.trim() && (
                <span className="text-sm text-muted">Keine offene Bestellung mit dieser Nummer</span>
              )
            )}
          </div>

          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead className="bg-panel text-left">
                <tr>
                  <th className="p-2">Sternnummer</th>
                  <th className="p-2">Gesamtmenge <span className="font-normal text-muted">(Stück)</span></th>
                  <th className="p-2">Kartons <span className="font-normal text-muted">(gesamt)</span></th>
                  <th className="p-2">Datum <span className="font-normal text-muted">(leer = oben)</span></th>
                  <th className="p-2">Auflösung</th><th className="p-2">OK</th><th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.localId} className="border-t border-line">
                    <td className="p-2">
                      <Input className="w-32" value={row.artikel}
                        onChange={(e) => updateRow(row.localId, { artikel: e.target.value, status: 'pending', confirmed: false, label: null })}
                        onBlur={() => void resolveRow(row.localId, row.artikel)} />
                    </td>
                    <td className="p-2">
                      {row.lagereinheit === 'meter' ? <div className="space-y-1">
                        <label className="text-xs">Rollenlänge<select className="block border border-line rounded-md px-2 py-1 text-sm bg-panel focus-visible:ring-ring/50 focus-visible:ring-[3px]" value={row.rollenlaenge || ''} onChange={(e) => {
                          const rollenlaenge = +e.target.value
                          const vorschlag = row.importOffen ? importRollen(row.importMenge, row.cartonsDelivered, rollenlaenge) : null
                          updateRow(row.localId, { rollenlaenge, confirmed: false, ...(row.importOffen ? { rollen: vorschlag ?? 0 } : {}) })
                        }}>
                          <option value="">Bitte wählen</option>{[1, 2, 5, 10, 30].map(n => <option key={n} value={n}>{n} m</option>)}
                        </select></label>
                        <label className="text-xs">Rollen je Karton<Input type="number" min={1} step={1} value={row.rollen || ''} onChange={(e) => updateRow(row.localId, { rollen: +e.target.value, confirmed: false, importOffen: false })} /></label>
                        {row.importOffen && row.importMenge && importRollen(row.importMenge, row.cartonsDelivered, row.rollenlaenge ?? 0) === null && <div className="text-xs text-muted">Import: {row.importMenge} m – Rollen je Karton eingeben</div>}
                        {!row.rollenlaenge && <span className="text-red-600 text-xs">Netz: Rollenlänge wählen</span>}
                        {row.rollenlaenge === 30 && row.rollen !== 1 && <span className="text-red-600 text-xs">R30: Eine Rolle je Karton eingeben</span>}
                        {rowQtyValid(row) && <div>{netzMenge(row)} m ({row.cartonsDelivered} × {row.rollen} Rollen à {row.rollenlaenge} m)</div>}
                        {rowQtyValid(row) && <div className="text-xs text-muted">{row.rollen! * row.rollenlaenge!} m je Karton</div>}
                      </div> : <Input className="w-20" type="number" min={1} step={1} value={row.totalQty || ''}
                        onChange={(e) => updateRow(row.localId, { totalQty: +e.target.value, confirmed: false })} />}
                    </td>
                    <td className="p-2">
                      <Input aria-label={row.lagereinheit === 'meter' ? 'Kartons' : undefined} className="w-20" type="number" min={1} step={1} value={row.cartonsDelivered || ''}
                        onChange={(e) => {
                          const cartonsDelivered = +e.target.value
                          const rollen = row.importOffen && row.lagereinheit === 'meter' ? importRollen(row.importMenge, cartonsDelivered, row.rollenlaenge ?? 0) : null
                          updateRow(row.localId, { cartonsDelivered, confirmed: false, ...(row.importOffen && row.lagereinheit === 'meter' ? { rollen: rollen ?? 0 } : {}) })
                        }} />
                    </td>
                    {/* Datum je Position (Migration 0094): leer -> Kopf-Datum landet auf dem
                        Etikett; gesetzt -> dieses Datum gewinnt. So kann ein Auftrag mehrere
                        Positionen mit unterschiedlichen Wareneingangs-Daten tragen. */}
                    <td className="p-2">
                      <Input className="w-36" type="date" value={row.firstInDate}
                        title="Leer lassen = Datum aus dem Kopf des Auftrags"
                        onChange={(e) => updateRow(row.localId, { firstInDate: e.target.value, confirmed: false })} />
                    </td>
                    <td className="p-2 text-xs">
                      {row.status === 'pending' && row.artikel.trim() !== '' && <span className="text-muted">prüfe…</span>}
                      {row.status === 'ok' && <span className="text-green-700">{row.label}</span>}
                      {row.status === 'error' && <span className="text-red-600">{row.label ?? 'nicht gefunden'}</span>}
                    </td>
                    <td className="p-2">
                      {row.status === 'ok' && !row.confirmed && (
                        <Button size="sm" variant="outline" disabled={!rowQtyValid(row)} onClick={() => confirmRow(row.localId)}>OK</Button>
                      )}
                      {row.confirmed && <span className="text-green-700 text-xs font-semibold">✓ bestätigt</span>}
                    </td>
                    <td className="p-2">
                      <Button variant="ghost" onClick={() => removeRow(row.localId)}>✕</Button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted">Noch keine Positionen — Zeile hinzufügen oder aus Excel einfügen.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 items-center flex-wrap">
            <Button variant="outline" onClick={addManualRow}>+ Zeile</Button>
            <span className="text-sm text-muted">{rows.length} Position{rows.length === 1 ? '' : 'en'}</span>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-semibold">Aus Excel einfügen</div>
            <Textarea
              className="min-h-20"
              placeholder="Aus Excel einfügen (Spalten: beliebig — Zuordnung folgt)"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              onPaste={(e) => {
                const raw = e.clipboardData.getData('text')
                if (raw) onPasteTextarea(raw)
              }}
            />
          </div>

          {pasteGrid && (
            <div className="rounded-xl border border-line bg-panel p-3 space-y-2">
              <div className="text-sm font-semibold">Spalten zuordnen ({pasteDataRows(pasteGrid).length} Zeile{pasteDataRows(pasteGrid).length === 1 ? '' : 'n'} erkannt)</div>
              <div className="flex flex-wrap gap-3 items-end">
                <label className="text-sm">Artikel
                  <select className="block border border-line rounded-md px-2 py-1 text-sm bg-panel"
                    value={mapping.artikel} onChange={(e) => setMapping({ ...mapping, artikel: +e.target.value })}>
                    {Array.from({ length: pasteGrid.colCount }, (_, i) => (
                      <option key={i} value={i}>{colLabel(i)}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">Gesamtmenge
                  <select className="block border border-line rounded-md px-2 py-1 text-sm bg-panel"
                    value={mapping.menge} onChange={(e) => setMapping({ ...mapping, menge: +e.target.value })}>
                    {Array.from({ length: pasteGrid.colCount }, (_, i) => (
                      <option key={i} value={i}>{colLabel(i)}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">Kartons
                  <select className="block border border-line rounded-md px-2 py-1 text-sm bg-panel"
                    value={mapping.kartons} onChange={(e) => setMapping({ ...mapping, kartons: +e.target.value })}>
                    {Array.from({ length: pasteGrid.colCount }, (_, i) => (
                      <option key={i} value={i}>{colLabel(i)}</option>
                    ))}
                  </select>
                </label>
                <Button onClick={applyPasteMapping}>Übernehmen</Button>
                <Button variant="ghost" onClick={cancelPasteMapping}>Abbrechen</Button>
              </div>
            </div>
          )}

          <Button onClick={() => void onCreate()} disabled={create.isPending || !allConfirmedOk}>
            Auftrag anlegen ({rows.length} Position{rows.length === 1 ? '' : 'en'})
          </Button>
        </div>
      )}

      <ReceiptOverview rows={receiptRows} loading={receipts.isLoading} />
    </div>
  )
}
